import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { TrialExpiredBlock } from "./TrialExpiredBlock";
import { Navbar } from "./Navbar";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Pages that remain accessible even after trial expires
const TRIAL_EXEMPT_PATHS = [
  "/billing",
  "/pricing",
  "/profile",
  "/company-settings",
];

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, loading: authLoading, authUser, signOut } = useAuth();
  const { isTrialExpired, loading: trialLoading } = useTrialStatus();
  const location = useLocation();

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve language prefix in redirect
    const langMatch = location.pathname.match(/^\/(en|et)/);
    const langPrefix = langMatch ? langMatch[0] : '/en';
    return <Navigate to={`${langPrefix}/auth`} state={{ from: location }} replace />;
  }

  // Check if current path is exempt from trial enforcement (strip lang prefix)
  const pathWithoutLang = location.pathname.replace(/^\/(en|et)/, '') || '/';
  const isExempt = TRIAL_EXEMPT_PATHS.some(p => pathWithoutLang.startsWith(p));

  // Show loading while checking trial status (only for non-exempt paths)
  if (!isExempt && trialLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Checking access...</p>
        </div>
      </div>
    );
  }

  // Block expired trial users on non-exempt pages
  if (!isExempt && isTrialExpired) {
    return (
      <div className="min-h-screen bg-background">
        {authUser && (
          <Navbar
            user={{
              name: authUser.name,
              email: authUser.email,
              role: authUser.role,
              createdAt: authUser.createdAt,
            }}
            onLogout={signOut}
          />
        )}
        <TrialExpiredBlock />
      </div>
    );
  }

  return <>{children}</>;
}
